
/**
 * Utilities for refreshing the PWA application.
 * Safari-specific: clears SW caches via postMessage before reload.
 */
import { isPushActivitySuspended } from './push-activity';

const isSafari = (): boolean =>
  /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

// Ask the SW to clear all caches, wait for ack (with timeout)
const clearSwCaches = (): Promise<void> => {
  return new Promise((resolve) => {
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
      resolve();
      return;
    }

    const channel = new MessageChannel();
    const timeout = setTimeout(() => resolve(), 2000);

    channel.port1.onmessage = () => {
      clearTimeout(timeout);
      resolve();
    };

    navigator.serviceWorker.controller.postMessage(
      { type: 'CLEAR_CACHES' },
      [channel.port2]
    );
  });
};

// Force a complete PWA refresh
export const forceAppRefresh = async (): Promise<void> => {
  if ((window as any).__reloadTriggered) return;

  // Defer reloads while the push pipeline is mid-handshake. The reload would
  // unregister/replace the SW that just received an APNs token, causing the
  // exact "accepted but never delivered" loop we're debugging.
  if (isPushActivitySuspended()) {
    console.log('[Version] Refresh deferred — push activity in flight');
    // Re-attempt after the suspension window most likely closes.
    setTimeout(() => {
      (window as any).__reloadTriggered = false;
      void forceAppRefresh();
    }, 30_000);
    return;
  }

  (window as any).__reloadTriggered = true;

  console.log('[Version] Forcing application refresh...');

  // Tell waiting SW to activate
  try {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
    }
  } catch (e) {
    console.error('[Version] SKIP_WAITING error:', e);
  }

  // Clear SW caches (waits for ack on Safari, 2s timeout)
  await clearSwCaches();

  // Also clear from client side as fallback
  if ('caches' in window) {
    try {
      const names = await caches.keys();
      await Promise.all(names.map(n => caches.delete(n)));
      console.log('[Version] Client-side caches cleared');
    } catch (e) {
      console.error('[Version] Cache clearing error:', e);
    }
  }

  performReload();
};

const performReload = (): void => {
  if (isSafari()) {
    // Inject meta tag to hint Safari not to serve from HTTP cache
    const meta = document.createElement('meta');
    meta.httpEquiv = 'Cache-Control';
    meta.content = 'no-cache, no-store, must-revalidate';
    document.head.appendChild(meta);

    // Navigate with cache-bust param
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('_t', String(Date.now()));
      window.location.href = url.toString();
    } catch {
      window.location.reload();
    }
  } else {
    window.location.replace(
      `${window.location.origin}${window.location.pathname}?refresh=${Date.now()}&forceUpdate=true`
    );
  }
};
